import { useMutation } from '@apollo/client';
import { useElementDimensionObserver } from '@saucelabs/sl-util-helpers';
import React, { useEffect, useRef } from 'react';

import { actions, selectors } from '../../ducks/diffUi';
import { DiffUiDisplayType } from '../../ducks/diffUi/constants';
import { ImageType } from '../../global/constants';
import { graphql, useFragment } from '../../graphql/__generated__';
import {
  BuildFragFragment,
  DiffFragFragment,
  DiffStatus,
  RectFragFragment,
  RegionFragFragment,
  UpdateDiffStatus,
} from '../../graphql/__generated__/graphql';
import { useVisualDispatch, useVisualSelector } from '../../reduxHooks';
import { filterDiffNotQueued } from '../VisualBuildDetailsPage';
import DiffLabelsTitle from './DiffLabelsTitle';
import DiffReviewButtons from './DiffReviewButtons';
import { DiffStage, DiffStageMetaFragment, DiffStageProps } from './DiffStage';
import DiffStageMeta, { DiffStageMetaProps } from './DiffStageMeta';
import { ChangeIndicator } from './components/ChangeIndicator';
import DiffPrefetch from './DiffPrefetch';
import DownloadImage from '../common/DownloadImage';
import DiffViewAnimation from './DiffViewAnimation';

export const DiffViewSnapshotFragment = graphql(`
  fragment DiffView_Snapshot on Snapshot {
    id
    name
    testName
    suiteName
    branch
    browser
    browserVersion
    device
    operatingSystem
    operatingSystemVersion
    viewportWidth
    viewportHeight
    imageUrl
    jobUrl
    ignoreRegions {
      ...RegionFrag
    }
  }
`);

export const DiffViewBaselineFragment = graphql(`
  fragment DiffView_Baseline on Baseline {
    id
    name
    testName
    suiteName
    browser
    browserVersion
    operatingSystem
    operatingSystemVersion
    viewportWidth
    viewportHeight
    imageUrl
    createdAt
    createdByUser {
      id
      username
    }
  }
`);

export const RegionFrag = graphql(`
  fragment RegionFrag on Region {
    x
    y
    width
    height
    name
  }
`);

export const RectFrag = graphql(`
  fragment RectFrag on Rect {
    x
    y
    width
    height
  }
`);

export const DiffViewFragment = graphql(`
  fragment DiffViewFragment on Diff {
    id
    createdAt
    status
    updatedByUser {
      id
      username
    }
    updatedAt
    diffBounds {
      ...RectFrag
    }
    diffClusters {
      ...RectFrag
    }
    baseline {
      ...DiffView_Baseline
    }
    snapshot {
      ...DiffView_Snapshot
    }
    ...DiffStageMetaFragment
  }
`);

const UpdateDiffMutation = graphql(/* GraphQL */ `
  mutation UpdateDiffMutation($uuid: UUID!, $status: UpdateDiffStatus!, $filter: DiffFilter!) {
      updateDiff(input: { uuid: $uuid, status: $status }) {
          ...DiffStageMetaFragment
          build {
            ...BuildFrag
          }
      }
  }
`);

type Props = {
  build: BuildFragFragment;
  diff: DiffFragFragment;
  nextPage: () => void;
  prevId?: string;
  nextId?: string;
}

const DiffView: React.FC<Props> = (props) => {
  const diffView = useFragment(DiffViewFragment, props.diff);
  const stageDiff = useFragment(DiffStageMetaFragment, props.diff);
  const baseline = useFragment(DiffViewBaselineFragment, diffView.baseline);
  const snapshot = useFragment(DiffViewSnapshotFragment, diffView.snapshot)!;
  const diffClusters = stageDiff.diffClusters as RectFragFragment[];
  const dispatch = useVisualDispatch();
  const { displayType, dims } = useVisualSelector(selectors.getDiffUi);

  const [updateDiff] = useMutation(UpdateDiffMutation);
  const commonVariables = {
    uuid: props.diff?.id,
    filter: filterDiffNotQueued,
  };
  const accept = () => updateDiff({
    variables: {
      ...commonVariables,
      status: UpdateDiffStatus.Approved,
    },
    optimisticResponse: {
      updateDiff: {
        ...{
          ...stageDiff,
          status: UpdateDiffStatus.Approved,
          updatedByUser: { ...stageDiff.updatedByUser, username: null },
          updatedAt: null,
        },
        build: {
          ...props.build,
        },
      },
    },
  });

  const reject = () => updateDiff({
    variables: {
      ...commonVariables,
      status: UpdateDiffStatus.Rejected,
    },
    optimisticResponse: {
      updateDiff: {
        ...{
          ...stageDiff,
          status: UpdateDiffStatus.Rejected,
          updatedByUser: { ...stageDiff.updatedByUser, username: null },
          updatedAt: null,
        },
        build: {
          ...props.build,
        },
      },
    },
  });

  const unapprove = () => updateDiff({
    variables: {
      ...commonVariables,
      status: UpdateDiffStatus.Unapproved,
    },
    optimisticResponse: {
      updateDiff: {
        ...{ ...stageDiff, status: UpdateDiffStatus.Unapproved },
        build: {
          ...props.build,
        },
      },
    },
  });

  const stageRef = useRef<HTMLDivElement>(null);
  const { elementDims } = useElementDimensionObserver(stageRef);

  useEffect(() => {
    // Prevent removing dims after animation
    if (!stageRef.current) {
      return;
    }

    const { width, height } = elementDims;
    dispatch(actions.setStageDims({
      // eslint-disable-next-line no-nested-ternary
      width: width > 0 ? (
        displayType === DiffUiDisplayType.SideBySide ? width / 2 : width
      ) : 1,
      height: height > 0 ? height : 1,
    }));
  }, [dispatch, displayType, elementDims]);

  const stageDims: Omit<DiffStageProps, 'type' | 'url' | 'animationState'> = {
    diff: stageDiff,
    ignoreRegions: snapshot.ignoreRegions as RegionFragFragment[],
  };

  const metaProps: Record<ImageType, DiffStageMetaProps> = {
    [ImageType.BASELINE]: {
      status: DiffStatus.Approved,
      updatedAt: stageDiff.baseline?.createdAt,
      updatedBy: stageDiff.baseline?.createdByUser.username ?? undefined,
      branch: stageDiff.baseline?.branch ?? undefined,
      browser: stageDiff.baseline?.browser ?? undefined,
      browserVersion: stageDiff.baseline?.browserVersion ?? undefined,
      operatingSystem: stageDiff.baseline?.operatingSystem ?? undefined,
      operatingSystemVersion: stageDiff.baseline?.operatingSystemVersion ?? undefined,
      device: stageDiff.baseline?.device ?? undefined,
      viewportHeight: stageDiff.baseline?.viewportHeight ?? undefined,
      viewportWidth: stageDiff.baseline?.viewportWidth ?? undefined,
    },
    [ImageType.SNAPSHOT]: {
      status: stageDiff.status,
      updatedAt: stageDiff.updatedAt,
      updatedBy: stageDiff.updatedByUser?.username ?? undefined,
      branch: stageDiff.snapshot?.branch ?? undefined,
      browser: stageDiff.snapshot?.browser ?? undefined,
      browserVersion: stageDiff.snapshot?.browserVersion ?? undefined,
      operatingSystem: stageDiff.snapshot?.operatingSystem ?? undefined,
      operatingSystemVersion: stageDiff.snapshot?.operatingSystemVersion ?? undefined,
      device: stageDiff.snapshot?.device ?? undefined,
      viewportHeight: stageDiff.snapshot?.viewportHeight ?? undefined,
      viewportWidth: stageDiff.snapshot?.viewportWidth ?? undefined,
    },
  };

  return (
    <>
      <div className="flex flex-row shadow-md z-10 items-center">
        <DiffLabelsTitle
          side={ImageType.BASELINE}
        >
          {!!baseline && (
            <>
              <DiffStageMeta
                {...metaProps[ImageType.BASELINE]}
              />
              <div className="ml-auto">
                <DownloadImage
                  id={baseline.id}
                  title="Download baseline"
                  type={ImageType.BASELINE}
                  url={baseline?.imageUrl}
                />
              </div>
            </>
          )}
        </DiffLabelsTitle>
        <DiffLabelsTitle
          side={ImageType.SNAPSHOT}
        >
          <DiffStageMeta
            {...metaProps[ImageType.SNAPSHOT]}
          />
          <DownloadImage
            className="ml-auto"
            id={snapshot.id}
            title="Download current"
            type={ImageType.SNAPSHOT}
            url={snapshot.imageUrl}
          />
          <ChangeIndicator
            clusters={diffClusters}
            hasBaseline={!!baseline}
          />
        </DiffLabelsTitle>
      </div>
      <div
        className="flex gap-2 flex-1 min-h-0 bg-gray-100 relative"
        data-qa="stage-wrapper"
      >
        <div className="flex flex-col flex-grow min-w-0 relative">
          <DiffReviewButtons
            diffId={diffView.id}
            diffStatus={diffView.status}
            nextPage={props.nextPage}
            onAccept={accept}
            onReject={reject}
            onUnapprove={unapprove}
          />

          <DiffViewAnimation nodeRef={stageRef}>
            {(animationState) => (
              <div
                ref={stageRef}
                className="flex h-full flex-row w-full absolute m-auto"
              >
                <DiffStage
                  animationState={animationState}
                  type={ImageType.BASELINE}
                  url={baseline?.imageUrl}
                  {...stageDims}
                />

                {displayType === DiffUiDisplayType.SideBySide && (
                  // TODO: Convert into draggable separator
                  <div className="h-full bg-gray-500 w-px relative z-10" />
                )}

                <DiffStage
                  animationState={animationState}
                  type={ImageType.SNAPSHOT}
                  url={snapshot?.imageUrl}
                  {...stageDims}
                />
              </div>
            )}
          </DiffViewAnimation>

        </div>
      </div>
      {(baseline?.imageUrl ? !!dims.BASELINE.width : true) && !!dims.SNAPSHOT.width && (
        <DiffPrefetch
          nextId={props.nextId}
          prevId={props.prevId}
        />
      )}
    </>
  );
};

DiffView.displayName = 'DiffView';

export default DiffView;
